import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { closeModal, openModal, showToastMessage } from 'enable-ui';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { MODAL_KEY } from 'modules/auth/core/modal-key';
import type { Lessons } from 'modules/dashboard/core/type';
import type { Institution } from 'modules/institutions/core/type';

import type { EventCalendar, LessonsInClass } from '../type';

import { getTimeLine } from 'helpers';
import { apiCall } from 'hooks/service';
import { cloneDeep, isUndefined } from 'lodash';

import { PATH } from 'constants/path';

import { defaultEventCalendar, defaultLessonsInClass } from '../constant';
import { LessonAssessmentType } from '../enum';
import { getLessonInClass } from '../helpers';

export const useClassCalendarController = () => {
  const userProfile = useAuthStore(userProfileSelector);

  const navigationTo = useNavigate();

  const params = useParams();

  const { id } = params;

  const institutionId = userProfile?.institution?.id || '';

  const institutionIdRef = useRef(institutionId);

  const [selectedLessons, setSelectedLessons] = useState<Record<string, string>>({});

  const [tooltipVisibility, setTooltipVisibility] = useState<Record<string, boolean>>({});

  const [myEvents, setMyEvents] = useState<EventCalendar[]>([]);

  const [draggedEvent, setDraggedEvent] = useState<EventCalendar | null>();

  const [lessonsInClass, setLessonsInClass] = useState<LessonsInClass>(defaultLessonsInClass);

  const [selectedAssessments, setSelectedAssessments] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!id) {
      return;
    }

    getListLessonInClass();
  }, []);

  const getListLessonInClass = async () => {
    const result: {
      unscheduled: Lessons[];
      scheduled: Lessons[];
      totalLearner: number;
      institution: Institution;
    } = await apiCall({
      url: `/classes/${id}/lessons`,
      method: 'GET',
      showError: true,
      isLoading: true,
    });

    if (result) {
      const newData = getLessonInClass(result.unscheduled, result.scheduled, result.totalLearner);

      setLessonsInClass({
        ...newData,
        institution: result?.institution,
      });

      institutionIdRef.current = result?.institution?.id ?? '';

      const newEvents = [...newData.today, ...newData.future, ...newData.past].map((el) => ({
        id: el.id,
        title: el.name,
        type: el.type?.name,
        start: el.start ? new Date(el.start) : '',
        end: el.end ? new Date(el.end) : '',
        classLessonId: el.classLessonId,
        classScheduleId: el.classScheduleId,
        lessonType: el.lessonType,
      }));

      setMyEvents(newEvents);
    }
  };

  const lessonsData = lessonsInClass?.unscheduled.map((el) => ({
    id: el.id,
    title: el.name,
    type: el.type?.name,
    start: el.start ?? '',
    end: el.end ?? '',
    classLessonId: el.classLessonId,
    classScheduleId: el.classScheduleId,
    lessonType: el.lessonType,
    totalExercises: el.totalExercises,
    totalAssessments: el.totalAssessments,
  }));

  const handleDragStart = useCallback((event: EventCalendar) => setDraggedEvent(event), []);

  const newEvent = useCallback(
    (event: EventCalendar) => {
      setMyEvents((prev) => {
        return [...prev, { ...event }];
      });
    },
    [setMyEvents],
  );

  const onDropFromOutside = useCallback(
    ({ start = new Date(), end = new Date() }) => {
      const event = {
        id: draggedEvent?.id ?? '',
        title: draggedEvent?.title ?? '',
        type: draggedEvent?.type ?? '',
        classLessonId: draggedEvent?.classLessonId ?? '',
        classScheduleId: draggedEvent?.classScheduleId ?? '',
        start,
        end,
        lessonType: draggedEvent?.lessonType,
      };

      setDraggedEvent(event);

      openModal(MODAL_KEY.LESSON_INFO);
    },
    [draggedEvent, setDraggedEvent, newEvent],
  );

  const handleMoveEvent = async (event: EventCalendar, start: Date, end: Date) => {
    await apiCall({
      url: `/class-schedules/${event?.classScheduleId}`,
      method: 'PATCH',
      data: {
        start: start.toISOString(),
        end: end.toISOString(),
      },
      isLoading: true,
      showError: true,
    });

    getListLessonInClass();
  };

  const moveEvent = useCallback(
    ({ event = defaultEventCalendar, start = new Date(), end = new Date() }) => {
      setMyEvents((prev: EventCalendar[]) => {
        const existing = prev.find((ev) => ev.id === event.id) ?? {};
        const filtered = prev.filter((ev) => ev.id !== event.id);

        return [...filtered, { ...existing, start, end }] as EventCalendar[];
      });
      handleMoveEvent(event, start, end);
    },
    [setMyEvents],
  );

  const onSelectEvent = (event: EventCalendar) => {
    setDraggedEvent(event);
    openModal(MODAL_KEY.LESSON_INFO);
  };

  const onChangeSelectedLessons = (lesson: string) => () => {
    const newSelected = cloneDeep(selectedLessons);

    if (!isUndefined(newSelected[lesson])) {
      delete newSelected[lesson];
    } else {
      newSelected[lesson] = lesson;
    }

    setSelectedLessons(newSelected);
  };

  const handleAddLesson = async () => {
    const result: boolean = await apiCall({
      url: `/classes/${id}/add-lessons`,
      method: 'PATCH',
      data: {
        lessonIds: Object.keys(selectedLessons),
      },
    });

    if (result) {
      setSelectedLessons({});

      showToastMessage('success', 'Added lesson into class successfully!');

      getListLessonInClass();
    }
  };

  const handleTooltipToggle = (lessonId: string, event: EventCalendar) => {
    setTooltipVisibility((prevVisibility) => ({
      ...prevVisibility,
      [lessonId]: !prevVisibility[lessonId],
    }));

    setDraggedEvent(event);
  };

  const goToLessonDetail = (type: LessonAssessmentType) => {
    const path =
      type === LessonAssessmentType.ASSESSMENTS ? PATH.ASSESSMENT_DETAIL : PATH.LESSON_DETAIL;

    navigationTo(`${PATH.CLASSES}/${PATH.CLASSES_DETAIL.replace(':id', id ?? '')}/${PATH.CLASSES_LESSONS}/${path.replace(':id', draggedEvent?.id ?? '')}
    `);
    closeModal(MODAL_KEY.LESSON_INFO);
  };

  const handleDuplicateLesson = async () => {
    if (!draggedEvent?.classLessonId) {
      return;
    }

    const result: { id: string } = await apiCall({
      url: `/classes/${id}/duplicate-lesson/${draggedEvent.classLessonId}`,
      method: 'POST',
      isLoading: true,
      showError: true,
    });

    if (result?.id) {
      showToastMessage('success', 'Lesson duplicated successfully!');

      getListLessonInClass();

      setTooltipVisibility({});
    }
  };

  const handleClickDeleteLesson = () => {
    closeModal(MODAL_KEY.LESSON_INFO);
    openModal(MODAL_KEY.DELETE_LESSON_IN_CLASS);
    setTooltipVisibility({});
  };

  const handleDeleteLesson = async () => {
    if (draggedEvent?.classLessonId) {
      const result: boolean = await apiCall({
        url: `/classes/${id}/delete-lesson/${draggedEvent.classLessonId}`,
        method: 'DELETE',
        isLoading: true,
        showError: true,
      });

      if (result) {
        closeModal(MODAL_KEY.DELETE_LESSON_IN_CLASS);
        showToastMessage('success', 'Removed lesson successfully!');
        getListLessonInClass();
      }
    }
  };

  const handleUpdateEvent = async () => {
    let result: any;

    if (draggedEvent?.classScheduleId) {
      result = await apiCall({
        url: `/class-schedules/${draggedEvent.classScheduleId}`,
        method: 'PATCH',
        data: {
          start: draggedEvent?.start,
          end: draggedEvent?.end,
        },
        isLoading: true,
        showError: true,
      });
    } else {
      result = await apiCall({
        url: `/class-schedules`,
        method: 'POST',
        data: {
          data: [
            {
              classLessonId: draggedEvent?.classLessonId,
              start: draggedEvent?.start,
              end: draggedEvent?.end,
            },
          ],
        },
        isLoading: true,
        showError: true,
      });
    }

    setDraggedEvent(null);

    getListLessonInClass();

    closeModal(MODAL_KEY.LESSON_INFO);

    if (result?.id || result?.length) {
      showToastMessage('success', 'Updated schedule successfully!');
    }
  };

  const handleChangeValue = (date: Date, startTime: string, endTime: string) => {
    setDraggedEvent({
      ...draggedEvent,
      start: getTimeLine(date, startTime).toISOString(),
      end: getTimeLine(date, endTime).toISOString(),
    } as EventCalendar);
  };

  const onChangeSelectedAssessments = (assessment: string) => () => {
    const newSelected = cloneDeep(selectedAssessments);

    if (!isUndefined(newSelected[assessment])) {
      delete newSelected[assessment];
    } else {
      newSelected[assessment] = assessment;
    }

    setSelectedAssessments(newSelected);
  };

  const handleAddAssessment = async () => {
    const result: boolean = await apiCall({
      url: `/classes/${id}/add-lessons`,
      method: 'PATCH',
      data: {
        assessmentIds: Object.keys(selectedAssessments),
      },
    });

    if (result) {
      setSelectedAssessments({});
      getListLessonInClass();
      showToastMessage('success', 'Added assessment into class successfully!');
    }
  };

  return {
    myEvents,
    onDropFromOutside,
    lessonsData,
    handleDragStart,
    moveEvent,
    onSelectEvent,
    selectedLessons,
    onChangeSelectedLessons,
    handleAddLesson,
    tooltipVisibility,
    handleTooltipToggle,
    goToLessonDetail,
    handleDuplicateLesson,
    handleClickDeleteLesson,
    handleDeleteLesson,
    draggedEvent,
    handleChangeValue,
    handleUpdateEvent,
    selectedAssessments,
    onChangeSelectedAssessments,
    handleAddAssessment,
    institutionId: institutionIdRef.current,
  };
};
